import {DepartmentsClient} from '@client/departments-client'
import {Sync} from '@client/sync'
import {MenuItem} from '@components/common/base/dropdown-list'
import {TableUserInfo} from '@components/common/users-table/users-table'
import {UsersTableUtil} from '@components/common/users-table/users-table-util'
import {DepartmentEditorProps} from '@components/departments-edit/department-editor'
import {MessageBox} from '@components/message-box'
import {makeAutoObservable} from 'mobx'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  Department,
  getParentDepartments,
  isDepartmentInsideOf
} from 'yougile-model'
import {ArrUtil} from 'yougile-util'

export type Role = 'manager' | 'member'

interface State {
  departmentTitle: string
  parentId?: DataObjectId
  roles: Map<DataObjectId, Role>
  users: TableUserInfo[]
  selected: DataObjectId[]
  parentDepartments: MenuItem<DataObjectId>[]
  errors: {
    departmentTitle: string
  }
}

export class DepartmentEditorStore {
  state: State = {
    departmentTitle: '',
    roles: new Map(),
    users: [],
    selected: [],
    parentDepartments: [],
    errors: {
      departmentTitle: ''
    }
  }

  constructor(props: DepartmentEditorProps) {
    makeAutoObservable(this)

    const box = Sync.getCurrentSandbox()
    this.state.departmentTitle = props.department?.title ?? ''
    this.state.users = props.allUsers.map(id =>
      UsersTableUtil.convertUser(box, box.getUser(id))
    )
    this.state.roles.clear()

    this.state.parentDepartments = this.getParentDepartmentOptions(props)

    props.allUsers.forEach(userId =>
      this.state.roles.set(
        userId,
        props?.department?.managers?.includes(userId) ? 'manager' : 'member'
      )
    )
    this.state.parentId = props.department?.parentId

    if (props.department) {
      this.state.selected = props.department.managers.concat(
        props.department.members
      )
    } else {
      this.state.selected = []
    }
  }

  private getParentDepartmentOptions(props: DepartmentEditorProps): (
    | {
        value: DataObjectId
        label: string
        iconChecked: boolean
      }
    | {
        value: undefined
        label: string
        iconChecked: boolean
      }
  )[] {
    const departments = props.allDepartments
      .filter(
        d =>
          props.department?.id !== d.id &&
          (props.isAdmin ||
            d.managers.includes(props.userId) ||
            !!getParentDepartments(d, props.allDepartments).find(d2 =>
              d2.managers.includes(props.userId)
            ))
      )
      .map(d => ({
        value: d.id,
        label: d.title,
        iconChecked: true
      }))
    return [
      {
        value: undefined,
        label: Lang.msg.component.departmentEditor.withoutParentDepartment,
        iconChecked: true
      },
      ...departments
    ]
  }

  createNewDepartment(): Department {
    const box = Sync.getCurrentSandbox()
    return DepartmentsClient.createDepartment(
      box,
      this.state.departmentTitle,
      this.state.parentId
    )
  }

  validate(): boolean {
    if (!this.state.departmentTitle) {
      this.state.errors.departmentTitle = 'error'
    } else {
      this.state.errors.departmentTitle = ''
    }
    return !this.state.errors.departmentTitle
  }

  updateDepartment(department: Department | undefined): Department | false {
    if (!this.validate()) {
      return false
    }
    const [managers, members] = ArrUtil.partition(
      this.state.selected,
      userId => this.state.roles.get(userId) === 'manager'
    )

    return {
      ...(department ?? this.createNewDepartment()),
      parentId: this.state.parentId,
      title: this.state.departmentTitle,
      managers,
      members
    }
  }

  setTitle(title: string): void {
    this.state.departmentTitle = title
    this.validate()
  }

  setRole(userId: DataObjectId, role: Role): void {
    const roles = new Map(this.state.roles)
    roles.set(userId, role)
    this.state.roles = roles
  }

  setParent(
    parentId: DataObjectId | undefined,
    props: DepartmentEditorProps
  ): void {
    const parent = parentId
      ? props.allDepartments.find(d => d.id === parentId)
      : undefined
    if (
      parent &&
      isDepartmentInsideOf(parent, props.department, props.allDepartments)
    ) {
      MessageBox.warn(Lang.msg.companies.departmentDisallowParentInsideChild)
      return
    }
    this.state.parentId = parentId
  }

  setSelected(selected: DataObjectId[]): void {
    this.state.selected = selected
  }
}
